import { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useLinkedAccounts } from "MVVM/Hooks/useLinkedAccounts";
import {
  CS2Filters,
  CS2MatchHistoryFilters,
  IsOwnMatchHistory,
  MatchHistoryGame,
  MatchHistoryplayerToSearchFor,
} from "MVVM/State/matchHistory";
import { AllstarUser } from "MVVM/State";

import { getPlayerGameIdByGame, getPlayerSearchIdentifier } from "./utils";
import {
  MATCHES_WITH_FILTERS,
  STEAM_FRIENDS,
} from "../../GraphQL/matchHistory";
import { ESupportedGames } from "MVVM/@types";

const PAGE_SIZE = 10;

const cs2FiltersToVariables = (filters: CS2Filters) => {
  const vars: {
    map?: string;
    platform?: string;
    wingman?: boolean;
    result?: string;
    minKills?: number;
    minMomentKills?: number;
  } = {};
  if (filters.map) vars.map = filters.map;
  if (filters.result) vars.result = filters.result;
  if (filters.minKills != null) vars.minKills = filters.minKills;
  if (filters.minMomentKills != null)
    vars.minMomentKills = filters.minMomentKills;
  if (filters.faceitOnly) vars.platform = "FACEIT";
  if (filters.wingmanOnly) vars.wingman = true;
  return vars;
};

const GameGraphMap: Record<ESupportedGames, string | undefined> = {
  505: "VALORANT",
  570: "DOTA2",
  101: "LOL",
  7302: "CS2",
  730: "CS_GO",
  407: "FORTNITE",
  2357570: "OW2",
  [ESupportedGames.ALL]: undefined,
  [ESupportedGames.NONE]: undefined,
};

export const MatchesViewmodel = (
  pageSize: number = PAGE_SIZE,
  playerGameIdentifier?: string,
  skip: boolean = false,
) => {
  const { hasEpicLinked, hasRiotLinked } = useLinkedAccounts();
  const [matchHistoryPlayerToSearchFor, setMatchHistoryPlayerToSearchFor] =
    useRecoilState(MatchHistoryplayerToSearchFor);
  const setIsOwnMatchHistory = useSetRecoilState(IsOwnMatchHistory);
  const allstarUser = useRecoilValue(AllstarUser);
  const matchHistoryGame = useRecoilValue(MatchHistoryGame);
  const cs2Filters = useRecoilValue(CS2MatchHistoryFilters);
  const playerIdFromUrl = getPlayerSearchIdentifier();

  const isCs2 = matchHistoryGame === ESupportedGames.CS2;
  const filterVariables = isCs2 ? cs2FiltersToVariables(cs2Filters) : {};

  const {
    data: matchesData,
    error: matchesError,
    loading: matchesLoading,
    fetchMore,
  } = useQuery(MATCHES_WITH_FILTERS, {
    variables: {
      playerGameId: matchHistoryPlayerToSearchFor,
      first: pageSize,
      game: GameGraphMap[matchHistoryGame],
      ...filterVariables,
    },
    skip: (!matchHistoryPlayerToSearchFor && !playerIdFromUrl) || skip,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  useEffect(() => {
    const playerToSearchFor =
      playerIdFromUrl ||
      playerGameIdentifier ||
      getPlayerGameIdByGame(matchHistoryGame, allstarUser);
    setMatchHistoryPlayerToSearchFor(playerToSearchFor);
  }, [
    allstarUser,
    matchHistoryGame,
    setMatchHistoryPlayerToSearchFor,
    matchHistoryPlayerToSearchFor,
    playerIdFromUrl,
    playerGameIdentifier,
  ]);

  useEffect(() => {
    const ourGameId = getPlayerGameIdByGame(matchHistoryGame, allstarUser);

    setIsOwnMatchHistory(ourGameId === matchHistoryPlayerToSearchFor);
  }, [
    matchHistoryPlayerToSearchFor,
    allstarUser,
    matchHistoryGame,
    setIsOwnMatchHistory,
  ]);

  const handleFetchMore = async () => {
    await fetchMore({
      variables: {
        after: matchesData?.matches?.pageInfo?.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          matches: {
            ...fetchMoreResult.matches,
            nodes: [
              ...(prev.matches?.nodes || []),
              ...(fetchMoreResult.matches?.nodes || []),
            ],
          },
        };
      },
    });
  };

  return {
    matches: matchesData?.matches?.nodes || [],
    matchesLoading,
    matchesError,
    noEpicLinked: !hasEpicLinked,
    noRiotLinked: !hasRiotLinked,
    matchHistoryPlayerToSearchFor,
    fetchMore: handleFetchMore,
    pageInfo: matchesData?.matches?.pageInfo,
  };
};

export const SteamFriendsViewModel = () => {
  const { hasSteamLinked } = useLinkedAccounts();
  const { data: friendsData, loading: friendsLoading } = useQuery(
    STEAM_FRIENDS,
    {
      skip: !hasSteamLinked,
      fetchPolicy: "cache-and-network",
    },
  );

  return {
    friendsData: friendsData?.steamFriends ?? [],
    friendsLoading,
  };
};
